import { Float } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { Observable } from 'rxjs';
import { Conversation } from '../conversation.js';
import { Exam, ExamState } from '../exam.js';

export const getAllExamsQuery = registerQuery({
  operationName: 'getAllExamsQuery',
  type: () => [Exam],
  // `limit`/`skip` are optional for pagination. When omitted, all exams are returned
  // (kept for callers such as the notification toaster that need the full set).
  params: [
    { name: 'limit', type: () => Float },
    { name: 'skip', type: () => Float },
  ],

  resolve:
    (limit?: number, skip?: number) =>
    async (_gqlFields: BaseQLSelectionSet<Exam>): Promise<Exam[]> => {
      // Filter exams by the current user to avoid leaking other users' exams
      const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
      const filter = currentUser?._id ? { userID: currentUser._id } : { userID: null };

      const pipeline: Record<string, unknown>[] = [
        { $match: filter },
        {
          $lookup: {
            from: Conversation._collectionName,
            localField: 'conversationId',
            foreignField: '_id',
            as: '_conv',
            pipeline: [{ $project: { lastUpdated: 1 } }],
          },
        },
        { $addFields: { _convLastUpdated: { $arrayElemAt: ['$_conv.lastUpdated', 0] } } },
        { $sort: { _convLastUpdated: -1, createdAt: -1 } },
      ];

      if (skip && skip > 0) pipeline.push({ $skip: Math.floor(skip) });
      if (limit && limit > 0) pipeline.push({ $limit: Math.floor(limit) });

      pipeline.push({ $project: { _conv: 0, _convLastUpdated: 0 } });

      const rawCollection = await getRawCollection(Exam._collectionName);
      const allExams = await rawCollection.aggregate(pipeline).toArray();
      return allExams as Exam[];
    },
});

export const getExamByIdQuery = registerQuery({
  operationName: 'getExamByIdQuery',
  type: () => Exam,
  params: [{ name: 'examId', type: () => String }],

  resolve:
    (examId: string) =>
    async (_gqlFields: BaseQLSelectionSet<Exam>): Promise<Exam | null> => {
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });
      return exam as Exam | null;
    },
});

export const streamExamByIdSubscription = registerSubscription({
  operationName: 'streamExamByIdSubscription',
  type: () => Exam,
  params: [{ name: 'examId', type: () => String }],

  resolve: (examId: string) => (_gqlFields: BaseQLSelectionSet<Exam>) =>
    new Observable<Exam>(subscriber => {
      const sub = watchQuery(Exam._collectionName, { _id: examId }, { limit: 1 }).subscribe({
        next: rows => {
          const exam = Array.isArray(rows) ? (rows[0] as Exam | undefined) : undefined;
          if (exam) subscriber.next(exam);
        },
        error: err => subscriber.error(err),
        complete: () => subscriber.complete(),
      });

      return () => {
        sub.unsubscribe();
      };
    }),
});

export const getExamStateByExamIdQuery = registerQuery({
  operationName: 'getExamStateByExamIdQuery',
  type: () => ExamState,
  params: [{ name: 'examId', type: () => String }],

  resolve:
    (examId: string) =>
    async (_gqlFields: BaseQLSelectionSet<ExamState>): Promise<ExamState | null> => {
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId }, { projection: { examState: 1 } });
      // Return the plain examState object from the DB
      return exam?.examState ?? null;
    },
});

export const streamExamStateByExamIdSubscription = registerSubscription({
  operationName: 'streamExamStateByExamIdSubscription',
  type: () => ExamState,
  params: [{ name: 'examId', type: () => String }],

  resolve: (examId: string) => (_gqlFields: BaseQLSelectionSet<ExamState>) =>
    new Observable<ExamState>(subscriber => {
      const sub = watchQuery(
        Exam._collectionName,
        { _id: examId },
        { limit: 1, projection: { examState: 1 } },
      ).subscribe({
        next: rows => {
          const examState = Array.isArray(rows) ? ((rows[0] as any)?.examState as ExamState | undefined) : undefined;
          if (examState) subscriber.next(examState);
        },
        error: err => subscriber.error(err),
        complete: () => subscriber.complete(),
      });

      return () => {
        sub.unsubscribe();
      };
    }),
});

export const streamExamStateWithTasksByExamIdSubscription = registerSubscription({
  operationName: 'streamExamStateWithTasksByExamIdSubscription',
  type: () => ExamState,
  params: [{ name: 'examId', type: () => String }],

  resolve: (examId: string) => (_gqlFields: BaseQLSelectionSet<ExamState>) =>
    new Observable<ExamState>(subscriber => {
      const sub = watchQuery(
        Exam._collectionName,
        { _id: examId },
        { limit: 1, projection: { examState: 1, tasks: 1 } },
      ).subscribe({
        next: rows => {
          const row = Array.isArray(rows) ? (rows[0] as any) : null;
          const examState = row?.examState ?? null;
          if (examState) {
            // Dedicated stream for clients that need structure data in the same event.
            subscriber.next({
              ...examState,
              tasks: Array.isArray(row?.tasks) ? row.tasks : [],
            });
          }
        },
        error: err => subscriber.error(err),
        complete: () => subscriber.complete(),
      });

      return () => {
        sub.unsubscribe();
      };
    }),
});
